import React, { FC, ReactNode, useState } from 'react';
import classnames from 'classnames';
import {
    AbstractSportEvent,
    sportEventQuery,
    TeamsSportEvent,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { useSearchParams } from '@/shared/hooks/useSearchParams';

import styles from './SportPlayerMeta.module.css';
import { SportTeamsEventMeta } from './_SportTeamsEventMeta';
import { SportBaseEventMeta } from './_SportBaseEventMeta';

const useSportPlayerMeta = () => {
    const searchParams = useSearchParams();
    const eventId = searchParams.get('sportEventId') ?? '';

    const { data } = useGqlQuery(sportEventQuery, {
        eventId,
    });

    return data?.event;
};

interface SportPlayerMetaProps {
    contentId: string;
    className?: string;
    children: (props: { onEnlargementChanged?: (isEnlarged: boolean) => void }) => ReactNode;
}

interface PlayerEventMetaProps {
    event: AbstractSportEvent | TeamsSportEvent;
}

const PlayerEventMetaComponent: FC<PlayerEventMetaProps> = ({ event }) => {
    if ('firstTeam' in event && 'secondTeam' in event) {
        return (
            <SportTeamsEventMeta
                competitionName={event.competition?.name}
                sportName={event.competition?.sportType.name}
                firstTeam={event.firstTeam}
                secondTeam={event.secondTeam}
            />
        );
    }

    return (
        <SportBaseEventMeta
            competitionName={event.competition?.name}
            sportName={event.competition?.sportType.name}
            eventTitle={event.title}
        />
    );
};

export const SportPlayerMeta: FC<SportPlayerMetaProps> = ({ className, children }) => {
    const sportEventMeta = useSportPlayerMeta();

    const [isMetaHidden, setIsMetaHidden] = useState(false);

    return (
        <section className={classnames(styles.root, className)}>
            {sportEventMeta && (
                <div className={classnames(styles.meta, { [styles.meta_hidden]: isMetaHidden })}>
                    <PlayerEventMetaComponent event={sportEventMeta} />
                </div>
            )}
            {children({ onEnlargementChanged: (isEnlarged) => setIsMetaHidden(isEnlarged) })}
        </section>
    );
};
